import { useTheme } from "expo-router/react-navigation";
import { Trophy } from "lucide-react-native";
import { FC, useMemo, useState } from "react";

import {
  Box,
  Card,
  Center,
  HStack,
  Media,
  MediaGallery,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import SampleEditorialSummary from "./_SampleEditorialSummary";

/* ── Types ─────────────────────────────────────────────── */

export interface ABTestReportData {
  /** One per option being tested. `themes` is an optional qualitative
      summary line shown in the "reasons" panel; cookers without a
      themes signal can leave it empty/null. `image_url` is an optional
      thumbnail (from the section's `media_urls`) shown next to each
      bar in the share-of-votes chart. */
  options: {
    id: string;
    image_url: string | null;
    name: string;
    themes: string | null;
  }[];
  /** Distinct respondents across the section, deduped on `persona.id`.
      Used for "(N respondents)" chart-title chips. */
  respondentCount: number;
  /** Vote counts in the same order as `options`. */
  votes: number[];
}

interface ABTestReportProps {
  data: ABTestReportData;
  showSampleEditorialSummary?: boolean;
}

/* ── Component ──────────────────────────────────────────── */

const ABTestReport: FC<ABTestReportProps> = ({
  data,
  showSampleEditorialSummary = false,
}) => {
  const { i18n } = useLocalization();
  const theme = useTheme();
  const primary = theme.colors.primary;

  const fmtPctRound = (n: number) =>
    i18n.numberToPercentage(n, { precision: 0 });

  const total = useMemo(
    () => (data.votes ?? []).reduce((s, v) => s + (v ?? 0), 0),
    [data.votes]
  );

  /* Sort by descending votes so the bar reads "winner first" left-to-right
     and the breakdown cards mirror that order. */
  const sortedOptions = useMemo(() => {
    return [...data.options]
      .map((o, idx) => ({ option: o, votes: data.votes[idx] ?? 0 }))
      .sort((a, b) => b.votes - a.votes);
  }, [data]);

  /* Lightbox: `galleryIndex < 0` means closed. The gallery only lists
     options that actually have an image, so we build a parallel index. */
  const galleryUris = useMemo(
    () =>
      sortedOptions
        .map(({ option }) => option.image_url)
        .filter((u): u is string => u != null),
    [sortedOptions]
  );
  const [galleryIndex, setGalleryIndex] = useState(-1);

  if (data.options.length === 0) {
    return null;
  }

  /* No votes yet: render the shell, show "—" instead of crowning a fake
     winner, and show a "No data available" block in place of the vote
     breakdown — mirroring how `PricingResult` shows "—" rather than a
     fabricated price. */
  const hasData = total > 0;

  const winner = sortedOptions[0];
  const winnerPct = hasData ? (winner.votes / total) * 100 : 0;

  return (
    <VStack className="gap-3">
      {showSampleEditorialSummary && (
        <SampleEditorialSummary
          body={i18n.t("useCasePage.preferenceTest.sampleReport.summary.body")}
          headline={i18n.t(
            "useCasePage.preferenceTest.sampleReport.summary.headline"
          )}
        />
      )}

      <Card className="p-6 rounded-2xl border border-outline-200">
        <VStack className="gap-3 lg:flex-row lg:items-center lg:gap-5">
          <VStack className="gap-2 lg:flex-1">
            <HStack className="items-center gap-4">
              <Box
                className="w-14 h-14 rounded-2xl items-center justify-center shrink-0"
                style={{ backgroundColor: primary }}
              >
                <Trophy color="white" size={26} strokeWidth={2.2} />
              </Box>
              <VStack className="flex-1 gap-1">
                <Text className="text-2xs font-semibold text-primary-600 uppercase tracking-wide">
                  {i18n.t(
                    "useCasePage.preferenceTest.sampleReport.winner.badge"
                  )}
                </Text>
                <Text className="text-xl font-bold text-typography-900 tracking-tight">
                  {hasData ? winner.option.name : "—"}
                </Text>
              </VStack>
            </HStack>
          </VStack>
          <VStack className="items-center lg:items-end gap-0">
            <Text
              className="text-4xl font-bold tracking-tight"
              style={{ color: primary }}
            >
              {hasData ? fmtPctRound(winnerPct) : "—"}
            </Text>
            <Text className="text-2xs font-semibold text-typography-400 uppercase tracking-wide">
              {i18n.t(
                "useCasePage.preferenceTest.sampleReport.winner.compositeLabel"
              )}
            </Text>
          </VStack>
        </VStack>
      </Card>

      <Card className="p-6 rounded-2xl border border-outline-200">
        <VStack className="gap-5">
          <VStack className="gap-1 pb-2">
            <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
              {`${i18n.t("useCasePage.preferenceTest.sampleReport.share.title")} (${i18n.t("dashboardNavigator.respondents", { count: data.respondentCount })})`}
            </Text>
            <Text className="text-xs text-typography-400">
              {i18n.t(
                "useCasePage.preferenceTest.sampleReport.share.subtitle",
                {
                  count: total,
                }
              )}
            </Text>
          </VStack>
          {!hasData ? (
            <Center className="py-12">
              <Text className="text-typography-500">
                {i18n.t("dashboardNavigator.noData")}
              </Text>
            </Center>
          ) : (
            <VStack className="gap-4">
              {sortedOptions.map(({ option, votes }, idx) => {
                const pct = (votes / total) * 100;
                const isWinner = option.id === winner.option.id;
                return (
                  <HStack className="items-center gap-3" key={option.id}>
                    {/* Thumbnail (or a neutral placeholder box when the option
                      has no image). Fixed size so the bars all start at the
                      same x regardless of whether images are attached.
                      Tapping opens the shared `MediaGallery` lightbox. */}
                    {option.image_url ? (
                      <Media
                        height={48}
                        onPress={() =>
                          setGalleryIndex(
                            galleryUris.indexOf(option.image_url!)
                          )
                        }
                        resizeMode="cover"
                        source={option.image_url}
                        width={48}
                      />
                    ) : (
                      <Box
                        className="rounded-lg bg-background-100"
                        style={{ height: 48, width: 48 }}
                      />
                    )}

                    <VStack className="flex-1 gap-2">
                      <HStack className="items-baseline justify-between">
                        <HStack className="items-center gap-2 flex-1">
                          <Text className="text-2xs font-semibold text-typography-400 uppercase tracking-wide w-7">
                            {String(idx + 1).padStart(2, "0")}
                          </Text>
                          <Text className="text-sm font-semibold text-typography-900">
                            {option.name}
                          </Text>
                        </HStack>
                        <HStack className="items-baseline gap-2 shrink-0">
                          <Text
                            className="text-lg font-bold tracking-tight"
                            style={{
                              color: isWinner
                                ? primary
                                : "rgb(var(--color-typography-700))",
                            }}
                          >
                            {fmtPctRound(pct)}
                          </Text>
                          <Text className="text-xs text-typography-400">
                            {i18n.t(
                              "useCasePage.preferenceTest.sampleReport.votesLabel",
                              {
                                count: votes,
                              }
                            )}
                          </Text>
                        </HStack>
                      </HStack>
                      <Box
                        className="w-full overflow-hidden rounded"
                        style={{
                          backgroundColor: "rgb(var(--color-background-100))",
                          height: 8,
                        }}
                      >
                        <Box
                          style={{
                            backgroundColor: isWinner
                              ? primary
                              : "rgb(var(--color-typography-300))",
                            height: "100%",
                            width: `${pct}%`,
                          }}
                        />
                      </Box>
                    </VStack>
                  </HStack>
                );
              })}
            </VStack>
          )}
        </VStack>
      </Card>

      {hasData &&
        sortedOptions.some(({ option }) => option.themes !== null) && (
          <Card className="p-6 rounded-2xl border border-outline-200">
            <VStack className="gap-5">
              <VStack className="gap-1 pb-2">
                <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
                  {`${i18n.t("useCasePage.preferenceTest.sampleReport.reasons.title")} (${i18n.t("dashboardNavigator.respondents", { count: data.respondentCount })})`}
                </Text>
                <Text className="text-xs text-typography-400">
                  {i18n.t(
                    "useCasePage.preferenceTest.sampleReport.reasons.subtitle"
                  )}
                </Text>
              </VStack>
              <VStack>
                {sortedOptions.map(({ option }, idx) => {
                  if (!option.themes) return null;
                  return (
                    <HStack
                      className={`items-start gap-4 py-4 ${idx > 0 ? "border-t border-outline-100" : ""}`}
                      key={option.id}
                    >
                      <Text className="text-2xs font-semibold text-typography-400 uppercase tracking-wide w-7 shrink-0">
                        {String(idx + 1).padStart(2, "0")}
                      </Text>
                      <VStack className="flex-1 gap-1">
                        <Text className="text-sm font-semibold text-typography-900">
                          {option.name}
                        </Text>
                        <Text className="text-xs text-typography-500 leading-relaxed">
                          {option.themes}
                        </Text>
                      </VStack>
                    </HStack>
                  );
                })}
              </VStack>
            </VStack>
          </Card>
        )}

      <MediaGallery
        initialIndex={Math.max(0, galleryIndex)}
        media={galleryUris}
        onClose={() => setGalleryIndex(-1)}
        visible={galleryIndex >= 0}
      />
    </VStack>
  );
};

export default ABTestReport;
